import React from 'react';
import { recordStudentActivity } from './studentActivityTelemetry';

/** Lesson speaking takes stop on their own after two minutes; the recording status shows this limit. */
export const STUDENT_SPEAKING_MAX_SECONDS = 120;

/** A recorder owned by one activity. Unsupported devices render recovery UI. */
export function useStudentSpeakingRecorder(onStop: (blob: Blob) => void, onError: (message: string) => void) {
    const [recording, setRecording] = React.useState(false);
    const [starting, setStarting] = React.useState(false);
    const [elapsed, setElapsed] = React.useState(0);
    React.useEffect(() => {
        if (!recording) return;
        const started = Date.now();
        const interval = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 250);
        return () => clearInterval(interval);
    }, [recording]);
    const recorder = React.useRef<MediaRecorder | null>(null);
    const stream = React.useRef<MediaStream | null>(null);
    const alive = React.useRef(true);
    const busy = React.useRef(false);
    const callback = React.useRef(onStop); callback.current = onStop;
    const timer = React.useRef<ReturnType<typeof setTimeout>>();
    const startedAt = React.useRef<number | null>(null);
    const stop = () => {
        if (startedAt.current !== null) {
            const duration = Math.round(performance.now() - startedAt.current);
            if (duration <= 3000) recordStudentActivity('short_microphone_hold', Math.max(0, duration));
            startedAt.current = null;
        }
        clearTimeout(timer.current);
        if (recorder.current?.state === 'recording') recorder.current.stop();
        stream.current?.getTracks().forEach(track => track.stop()); stream.current = null;
        if (alive.current) setRecording(false);
        busy.current = false;
    };
    React.useEffect(() => { alive.current = true; return () => { alive.current = false; stop(); }; }, []);
    const start = async () => {
        if (busy.current) return;
        busy.current = true; setStarting(true);
        try {
            if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') throw new Error('Recording is not supported in this browser. Try another browser or device.');
            const input = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (!alive.current) { input.getTracks().forEach(track => track.stop()); return; }
            stream.current = input;
            const mimeType = ['audio/webm', 'audio/mp4', 'audio/wav'].find(type => MediaRecorder.isTypeSupported(type));
            const capture = new MediaRecorder(input, mimeType ? { mimeType } : undefined);
            recorder.current = capture;
            const chunks: Blob[] = [];
            let failed = false;
            capture.ondataavailable = event => { if (event.data.size) chunks.push(event.data); };
            capture.onstop = () => {
                if (!alive.current || failed) return;
                const blob = new Blob(chunks, { type: capture.mimeType || mimeType || 'audio/webm' });
                if (blob.size) callback.current(blob);
                else onError('No recording was captured. Please try again.');
            };
            capture.onerror = () => { failed = true; stop(); if (alive.current) onError('Recording stopped unexpectedly. Please try again.'); };
            capture.start(); startedAt.current = performance.now(); setElapsed(0); setRecording(true);
            timer.current = setTimeout(stop, STUDENT_SPEAKING_MAX_SECONDS * 1000);
        } catch (error) {
            stop();
            if (alive.current) onError(error instanceof Error && error.name === 'NotAllowedError' ? 'Allow microphone access, then try again.'
                : error instanceof Error && error.name === 'NotFoundError' ? 'No microphone was found. Connect one and try again.'
                : error instanceof Error && error.message.startsWith('Recording is not supported') ? error.message
                : 'Microphone unavailable. Please try again.');
        } finally { if (alive.current) setStarting(false); }
    };
    return { recording, starting, elapsed, start, stop };
}
